
import React, { useEffect, useMemo, useState } from "react";

// === Mekânname Görev Takip Tablosu (.jsx) ===
// Tek dosya, Tailwind CSS ile şık arayüz. LocalStorage kalıcılık, filtreleme,
// arama, haftaya göre süzme, ilerleme yüzdesi, CSV dışa aktarma ve reset içerir.
// Varsayılan görev seti: 8 haftalık trafik artış planı.

const DEFAULT_START_DATE = () => {
  // Bugünden bir sonraki Pazartesi'yi varsayılan başlangıç alalım
  const d = new Date();
  const day = d.getDay(); // 0 pazar, 1 pazartesi...
  const diff = day === 1 ? 0 : (8 - day) % 7; // pazartesiye fark
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const RAW_WEEKS = [
  {
    week: 1,
    title: "Yazılım",
    tasks: [
      "Mekan taraması sırasında yükleniyor ikonu yerine dolan çubuk",
      "kullanıcıya giden hesabımı doğrula butonuna tıklayınca güzel bir sayfa açılması",
      "Tahmin edilen mekanın google yorumları",
      "Tahmin edilen mekanın çevresindeki cafe restaurant otel vs. konumların harita üzerinden işaretlenmesi",
      "Tahmin edilen mekanın içerdiği rotaları(eğer varsa) önermek",
      "Kullanıcıdan konum izni alarak yakınındaki tarihi kültürel mekanların işaretlenmesi",
      "Veritabanını PostgreSQL e çevirme",
    ]
  },
{
    week: 2,
    title: "Temel SEO & Teknik Temizlik",
    tasks: [
      "Tüm mekân sayfalarında title/meta/URL SEO uyumu",
      "sitemap.xml ve robots.txt güncelle",
      "Lighthouse raporu: performans/mobil sorunlarını düzelt",
      "Görselleri WebP + lazy-load uygula"
    ]
  },
  {
    week: 3,
    title: "Blog & İçerik Motoru",
    tasks: [
      "Blog modülünü yayına al",
      "5 SEO uyumlu liste içerik hazırla",
      "İçeriklerde harita ve yönlendirme linkleri kullan"
    ]
  },
  {
    week: 4,
    title: "Sosyal Medya Kaldıracı",
    tasks: [
      "10 kısa Reels/TikTok video hazırla",
      "Günlük 1 paylaşım (15–20 sn hikâye formatı)",
      "Kullanıcı fotoğrafları için #Mekanname akışı başlat"
    ]
  },
  {
    week: 5,
    title: "Yerel Odaklı PR (Ankara)",
    tasks: [
      "Yerel haber sitelerine basın bülteni gönder",
      "Turizm ofisi & üniversite kulüpleri ile iletişim",
      "3–4 yerel influencer ile barter anlaşması"
    ]
  },
  {
    week: 6,
    title: "Retention: Profil & E-posta",
    tasks: [
      "Profilde gezilen mekân listesi",
      "'Tamamladım' ve 'Favorilere ekle' butonları",
      "Haftalık e-posta bülteni başlat"
    ]
  },
  {
    week: 7,
    title: "Google Discover & Harici Platformlar",
    tasks: [
      "Discover uyumlu görsel ağırlıklı içerik üret",
      "TripAdvisor & Ekşi Sözlük tanıtım başlıkları",
      "Turizm bloglarına 2 konuk yazı"
    ]
  },
  {
    week: 8,
    title: "Viral İçerik Denemesi",
    tasks: [
      "TikTok: 'En Gizemli 5 Tarihi Mekân' serisi",
      "Instagram 'Haftanın Mekânı' arşivi",
      "UGC içerikleri hesapta öne çıkar"
    ]
  },
  {
    week: 9,
    title: "Ölçümleme & Optimizasyon",
    tasks: [
      "GA & Search Console analiz: en çok trafik çekenler",
      "Kazanan içeriklerin varyasyonlarını üret",
      "Sosyal format optimizasyonu (süre/konu tipi)"
    ]
  }
];

function buildInitialTasks() {
  let id = 1;
  return RAW_WEEKS.flatMap((w) =>
    w.tasks.map((t) => ({
      id: id++,
      title: t,
      week: w.week,
      phase: w.title,
      assignee: "",
      done: false,
      notes: "",
      dueOffsetDays: (w.week - 1) * 7 + 6 // her haftanın pazar günü
    }))
  );
}

const STORAGE_KEY = "mekanname_task_tracker_v1";

export default function TaskTracker() {
  const [startDate, setStartDate] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return saved?.startDate || DEFAULT_START_DATE();
  });
  const [tasks, setTasks] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return saved?.tasks || buildInitialTasks();
  });
  const [query, setQuery] = useState("");
  const [filterWeek, setFilterWeek] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tasks, startDate })
    );
  }, [tasks, startDate]);

  const parsedStart = useMemo(() => new Date(startDate + "T00:00:00"), [startDate]);

  const enriched = useMemo(() => {
    return tasks.map((t) => {
      const due = new Date(parsedStart);
      due.setDate(due.getDate() + t.dueOffsetDays);
      return { ...t, dueISO: due.toISOString().slice(0, 10) };
    });
  }, [tasks, parsedStart]);

  const filtered = useMemo(() => {
    return enriched.filter((t) => {
      const matchesQuery = t.title
        .toLowerCase()
        .includes(query.trim().toLowerCase());
      const weekOk = filterWeek === "all" || String(t.week) === String(filterWeek);
      const statusOk =
        filterStatus === "all" ||
        (filterStatus === "done" && t.done) ||
        (filterStatus === "todo" && !t.done);
      return matchesQuery && weekOk && statusOk;
    });
  }, [enriched, query, filterWeek, filterStatus]);

  const progress = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter((t) => t.done).length;
    return total ? Math.round((done / total) * 100) : 0;
  }, [tasks]);

  const weeks = useMemo(() => RAW_WEEKS.map((w) => w.week), []);

  function toggleTask(id) {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  }

  function updateAssignee(id, value) {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, assignee: value } : t)));
  }

  function updateNotes(id, value) {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, notes: value } : t)));
  }

  function exportCSV() {
    const headers = [
      "ID",
      "Task",
      "Week",
      "Phase",
      "Assignee",
      "Due",
      "Done",
      "Notes"
    ];
    const rows = enriched.map((t) => [
      t.id,
      escapeCSV(t.title),
      t.week,
      escapeCSV(t.phase),
      escapeCSV(t.assignee || ""),
      t.dueISO,
      t.done ? "YES" : "NO",
      escapeCSV(t.notes || "")
    ]);
    const csv =
      headers.join(",") +
      "\n" +
      rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mekanname_tasks_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }


  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6 sm:p-10">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-800">
              Mekânname Görev Takip Tablosu
            </h1>
            <p className="text-slate-600 mt-1">
              8 haftalık trafik artış planını askeri disiplinle yürüt. İşaretle, filtrele, ihraç et.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <label className="text-sm text-slate-600">Başlangıç Tarihi</label>
              <input
                type="date"
                className="mt-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <ProgressBadge value={progress} />
          </div>
        </div>

        {/* Controls */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Görevlerde ara…"
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2 pr-10 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">🔎</span>
            </div>
          </div>
          <select
            className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            value={filterWeek}
            onChange={(e) => setFilterWeek(e.target.value)}
          >
            <option value="all">Tüm Haftalar</option>
            {weeks.map((w) => (
              <option key={w} value={w}>
                {w}. Hafta
              </option>
            ))}
          </select>
          <select
            className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Durum: Hepsi</option>
            <option value="todo">Yapılacak</option>
            <option value="done">Tamamlandı</option>
          </select>
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={exportCSV}
            className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow hover:bg-slate-800 active:scale-[0.99]"
          >
            CSV Dışa Aktar
          </button>
          <button
            className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow hover:border-slate-400"
          >
            Sıfırla
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full table-fixed">
            <thead className="bg-slate-50">
              <tr>
                <Th className="w-12">#</Th>
                <Th>Görev</Th>
                <Th className="w-28">Hafta</Th>
                <Th className="w-32">Vade</Th>
                <Th className="w-40">Sorumlu</Th>
                <Th className="w-32">Durum</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    Filtreye uyan görev bulunamadı.
                  </td>
                </tr>
              )}
              {filtered.map((t, idx) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <Td>{t.id}</Td>
                  <Td>
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-800">{t.title}</span>
                      <span className="text-xs text-slate-500">{t.phase}</span>
                      <textarea
                        value={t.notes}
                        onChange={(e) => updateNotes(t.id, e.target.value)}
                        placeholder="Not ekle…"
                        className="mt-2 w-full resize-y rounded-xl border border-slate-200 bg-slate-50 p-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200"
                        rows={2}
                      />
                    </div>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                      {t.week}. Hafta
                    </span>
                  </Td>
                  <Td>
                    <div className="text-sm text-slate-700">{formatDateTR(t.dueISO)}</div>
                    <div className="text-xs text-slate-500">{t.dueISO}</div>
                  </Td>
                  <Td>
                    <input
                      type="text"
                      value={t.assignee}
                      onChange={(e) => updateAssignee(t.id, e.target.value)}
                      placeholder="Sorumlu kişi"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                    />
                  </Td>
                  <Td>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={t.done}
                        onChange={() => toggleTask(t.id)}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-300"
                      />
                      {t.done ? (
                        <span className="inline-flex items-center gap-1 rounded-xl bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                          Tamamlandı ✅
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-xl bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                          Yapılacak ⏳
                        </span>
                      )}
                    </label>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer tips */}
        <div className="mt-6 text-xs text-slate-500">
          İpucu: Başlangıç tarihini değiştirerek her haftanın otomatik vade tarihlerini güncelleyebilirsin. CSV ile ekip paylaşımı yap.
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={
        "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 " +
        className
      }
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={"px-4 py-4 align-top text-sm text-slate-700 " + className}>{children}</td>;
}

function ProgressBadge({ value }) {
  return (
    <div className="relative inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <ProgressRing value={value} size={44} stroke={6} />
      <div>
        <div className="text-xs text-slate-500">İlerleme</div>
        <div className="text-base font-semibold text-slate-800">%{value}</div>
      </div>
    </div>
  );
}

function ProgressRing({ value, size = 44, stroke = 6 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <svg width={size} height={size} className="block">
      <circle
        stroke="#e2e8f0"
        fill="transparent"
        strokeWidth={stroke}
        r={radius}
        cx={size / 2}
        cy={size / 2}
      />
      <circle
        stroke="#0f172a"
        fill="transparent"
        strokeWidth={stroke}
        strokeLinecap="round"
        r={radius}
        cx={size / 2}
        cy={size / 2}
        style={{ strokeDasharray: `${circumference} ${circumference}`, strokeDashoffset: offset, transition: "stroke-dashoffset 300ms ease" }}
      />
    </svg>
  );
}

function formatDateTR(iso) {
  try {
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("tr-TR", { weekday: "short", day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

function escapeCSV(val) {
  const s = String(val).replaceAll('"', '""');
  if (/[",\n]/.test(s)) return '"' + s + '"';
  return s;
}
